import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronLeft, Play, RotateCw, Lock, Loader2, Network, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const PHASE_COLORS = {
  open: "bg-blue-50 text-blue-700 border-blue-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
  upcoming: "bg-white text-slate-500 border-slate-200",
};

const PARTICIPANT_STATUS_COLORS = {
  not_started: "bg-slate-100 text-slate-600 border-slate-200",
  goals_drafting: "bg-amber-50 text-amber-700 border-amber-200",
  goals_submitted: "bg-blue-50 text-blue-700 border-blue-200",
  goals_approved: "bg-violet-50 text-violet-700 border-violet-200",
  goals_signed_off: "bg-emerald-50 text-emerald-700 border-emerald-200",
  in_progress: "bg-blue-50 text-blue-700 border-blue-200",
};

export default function CycleDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [cycle, setCycle] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const isAdmin = hasRole(user, "tenant_admin", "hr_admin");

  const load = () => {
    setLoading(true);
    Promise.all([api.get(`/cycles/${id}`), api.get(`/cycles/${id}/participants`)])
      .then(([c, p]) => {
        setCycle(c.data);
        setParticipants(p.data.items || []);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const activate = async () => {
    setActing(true);
    try {
      const r = await api.post(`/cycles/${id}/activate`);
      toast.success(`Cycle activated · ${r.data.participants} participants`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activation failed");
    } finally {
      setActing(false);
    }
  };

  const recompute = async () => {
    setActing(true);
    try {
      const r = await api.post(`/cycles/${id}/recompute-routing`);
      toast.success(`Routing recomputed · ${r.data.updated} updated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setActing(false);
    }
  };

  const close = async () => {
    setActing(true);
    try {
      await api.post(`/cycles/${id}/close`);
      toast.success("Cycle closed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setActing(false);
    }
  };

  if (loading || !cycle) return <div className="text-sm text-slate-500">Loading…</div>;

  const flagsByCount = participants.reduce((acc, p) => {
    (p.flags || []).forEach((f) => (acc[f] = (acc[f] || 0) + 1));
    return acc;
  }, {});

  return (
    <div className="max-w-7xl space-y-6" data-testid="cycle-detail-page">
      <Link to="/cycles" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5">
        <ChevronLeft className="w-3 h-3" /> Back to cycles
      </Link>

      <div className="axia-card p-6 lg:p-8">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span
                className={`axia-pill ${
                  cycle.status === "active"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : cycle.status === "draft"
                    ? "bg-amber-50 text-amber-700 border-amber-200"
                    : "bg-slate-100 text-slate-600 border-slate-200"
                }`}
              >
                {cycle.status}
              </span>
              <span className="axia-overline">{cycle.year}</span>
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-2">{cycle.name}</h1>
            <p className="text-sm text-slate-500 mt-1.5">Framework: <span className="font-medium text-slate-800">{cycle.framework_snapshot?.name}</span></p>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              {cycle.status === "draft" && (
                <Button onClick={activate} disabled={acting} data-testid="cycle-activate-btn">
                  {acting ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Play className="w-3.5 h-3.5 mr-2" />} Activate
                </Button>
              )}
              {cycle.status === "active" && (
                <>
                  <Button variant="outline" onClick={recompute} disabled={acting} data-testid="cycle-recompute-btn">
                    <RotateCw className="w-3.5 h-3.5 mr-2" /> Recompute routing
                  </Button>
                  <Button variant="outline" onClick={close} disabled={acting} data-testid="cycle-close-btn">
                    <Lock className="w-3.5 h-3.5 mr-2" /> Close cycle
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {(cycle.phases || []).map((p) => (
            <div key={p.id} className={`px-3 py-2 rounded-md border text-xs ${PHASE_COLORS[p.status]}`}>
              <div className="font-semibold">{p.label}</div>
              <div className="font-mono text-[10px] mt-0.5">
                {p.start_date} → {p.end_date} · {p.status}
              </div>
            </div>
          ))}
        </div>
      </div>

      {Object.keys(flagsByCount).length > 0 && (
        <div className="axia-card p-4 border-l-4 border-l-amber-400">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <div className="font-display font-semibold text-slate-900">Routing flags</div>
              <div className="text-xs text-slate-600 mt-1.5 flex flex-wrap gap-2">
                {Object.entries(flagsByCount).map(([k, v]) => (
                  <span key={k} className="axia-pill bg-amber-50 text-amber-700 border-amber-200">
                    {k.replace(/_/g, " ")} · {v}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="axia-card overflow-hidden" data-testid="participants-table">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="font-display font-semibold text-slate-900">Participants</h2>
            <div className="text-xs text-slate-500 mt-0.5">{participants.length} employees · routing computed from reporting chain</div>
          </div>
          <Network className="w-4 h-4 text-slate-400" />
        </div>
        <div className="max-h-[600px] overflow-auto">
          <Table>
            <TableHeader className="bg-slate-50 sticky top-0">
              <TableRow>
                <TableHead className="text-[10px] uppercase tracking-wide text-slate-500">Employee</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wide text-slate-500">Primary reviewer</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wide text-slate-500">Skip-level</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wide text-slate-500">HRBP</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wide text-slate-500">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wide text-slate-500">Flags</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {participants.map((p) => (
                <TableRow key={p.id} data-testid={`participant-row-${p.employee_id}`}>
                  <TableCell>
                    <Link to={`/profile/${p.employee_id}`} className="font-medium text-slate-900 hover:text-slate-700">
                      {p.employee_name}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm">{p.primary_reviewer_name || <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell className="text-sm">{p.skip_level_reviewer_name || <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell className="text-sm">{p.hrbp_name || <span className="text-slate-400">—</span>}</TableCell>
                  <TableCell>
                    <span className={`axia-pill ${PARTICIPANT_STATUS_COLORS[p.status] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
                      {(p.status || "").replace(/_/g, " ")}
                    </span>
                  </TableCell>
                  <TableCell>
                    {p.flags?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {p.flags.map((f) => (
                          <span key={f} className="axia-pill bg-amber-50 text-amber-700 border-amber-200">{f.replace(/_/g, " ")}</span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-emerald-600">ok</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}
